/**
 * @fileoverview TopBar de la console GNPC.
 *
 * Regroupe le trigger de sidebar, les notifications et le menu agent. Toute
 * action reste frontend-only dans cette tranche.
 */

import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Bell, KeyRound, LockKeyhole, LogOut, UserRound } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SidebarTrigger,
} from '@gnpc/ui';
import { useCurrentUser } from '../auth/CapabilityProvider.tsx';

interface ConsoleNotification {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  unread: boolean;
  tone: 'danger' | 'warning' | 'info';
  actionLabel: string;
  to: string;
}

const NOTIFICATIONS: ConsoleNotification[] = [
  {
    id: 'notif_payments_sti',
    title: 'Paiements à reprendre',
    description: "7 paiements encaissés attendent une reprise d'enregistrement Quipux.",
    createdAt: 'Il y a 12 min',
    unread: true,
    tone: 'danger',
    actionLabel: 'Voir les paiements',
    to: '/payments',
  },
  {
    id: 'notif_refund_review',
    title: 'Remboursement à valider',
    description: 'Une demande attend une validation quatre yeux.',
    createdAt: 'Il y a 34 min',
    unread: true,
    tone: 'warning',
    actionLabel: 'Ouvrir la file',
    to: '/refunds',
  },
  {
    id: 'notif_export_ready',
    title: 'Export disponible',
    description: "L'export CSV du rapprochement du 14 juillet est prêt.",
    createdAt: 'Hier',
    unread: false,
    tone: 'info',
    actionLabel: 'Voir les exports',
    to: '/exports',
  },
];

const NOTIFICATION_TONE_CLASSES: Record<ConsoleNotification['tone'], string> = {
  danger: 'border-danger/30 bg-danger/5 text-danger',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  info: 'border-quipux-blue-200 bg-quipux-blue-50 text-quipux-blue-700',
};

export function ConsoleTopBar(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [notifications, setNotifications] = useState(NOTIFICATIONS);
  const unreadCount = notifications.filter((notification) => notification.unread).length;

  return (
    <header className="border-border bg-background flex h-14 items-center gap-3 border-b px-4">
      <SidebarTrigger />
      <div className="min-w-0 flex-1" />
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Notifications" className="relative">
            <Bell />
            {unreadCount > 0 ? (
              /* `bg-primary` et non un orange brand direct : la pastille
               * affiche du texte blanc en 10 px — seul le token primary
               * (orange foncé accessible, ratio ≥ 4,5:1) garantit le
               * contraste exigé par axe `color-contrast`. Même règle que le
               * monogramme « G » de la sidebar. */
              <span className="bg-primary text-primary-foreground absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full text-[10px] font-semibold">
                {unreadCount}
              </span>
            ) : null}
          </Button>
        </SheetTrigger>
        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>Notifications</SheetTitle>
            <SheetDescription>
              Alertes opérationnelles et événements récents de la console.
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-3 p-5">
            {notifications.map((notification) => (
              <article key={notification.id} className="rounded-lg border border-border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 gap-3">
                    <span
                      aria-hidden="true"
                      className={`mt-1 size-2.5 shrink-0 rounded-full border ${NOTIFICATION_TONE_CLASSES[notification.tone]}`}
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-quipux-navy-950 font-medium">{notification.title}</p>
                        {notification.unread ? <Badge variant="outline">Nouveau</Badge> : null}
                      </div>
                      <p className="text-muted-foreground mt-1 text-sm leading-5">
                        {notification.description}
                      </p>
                    </div>
                  </div>
                  <span className="text-muted-foreground shrink-0 text-xs">{notification.createdAt}</span>
                </div>
                <SheetClose asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => navigate(notification.to)}
                  >
                    {notification.actionLabel}
                  </Button>
                </SheetClose>
              </article>
            ))}
          </div>

          <SheetFooter className="border-border border-t p-5">
            <Button
              variant="outline"
              disabled={unreadCount === 0}
              onClick={() =>
                setNotifications((current) =>
                  current.map((notification) => ({ ...notification, unread: false })),
                )
              }
            >
              Tout marquer comme lu
            </Button>
            <SheetClose asChild>
              <Button variant="outline">Fermer</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-10 gap-2 px-2">
            <Avatar className="size-8">
              <AvatarFallback className="bg-quipux-navy-100 text-quipux-navy-900 text-xs font-semibold">
                {user.initials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden text-left leading-tight md:block">
              <span className="text-quipux-navy-950 block text-sm font-medium">{user.fullName}</span>
              <span className="text-muted-foreground block text-xs">{user.role}</span>
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>
            <span className="block text-sm">{user.fullName}</span>
            <span className="text-muted-foreground block text-xs font-normal">{user.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/account/profile')}>
            <UserRound className="size-4" />
            Profil agent
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/account/security')}>
            <LockKeyhole className="size-4" />
            Sécurité du compte
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/account/mfa')}>
            <KeyRound className="size-4" />
            MFA et codes de secours
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/login')}>
            <LogOut className="size-4" />
            Se déconnecter
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
